import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  YoutubeLogo, Plus, Trash, Eye, Play, LinkSimple, Copy,
  ChartLineUp, CaretDown, Check, X as XIcon
} from '@phosphor-icons/react';

export default function YouTubeEmbeds() {
  const [embeds, setEmbeds] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [validated, setValidated] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', start: 0, autoplay: false, muted: false, loop: false });
  const [previewEmbed, setPreviewEmbed] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [embRes, anaRes] = await Promise.all([
        api.get('/youtube/embeds'),
        api.get('/youtube/analytics'),
      ]);
      setEmbeds(embRes.data || []);
      setAnalytics(anaRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const validateUrl = async () => {
    if (!urlInput) return;
    try {
      const res = await api.post('/youtube/validate-url', { url: urlInput });
      setValidated(res.data);
      if (!res.data.valid) toast.error(res.data.error || 'Invalid URL');
    } catch (err) {
      toast.error('Validation failed');
    }
  };

  const createEmbed = async () => {
    if (!validated?.valid) { toast.error('Validate URL first'); return; }
    try {
      await api.post('/youtube/embeds', {
        url: urlInput,
        title: form.title,
        description: form.description,
        start: form.start,
        autoplay: form.autoplay,
        muted: form.muted,
        loop: form.loop,
      });
      toast.success('YouTube embed created');
      setShowAdd(false);
      setUrlInput('');
      setValidated(null);
      setForm({ title: '', description: '', start: 0, autoplay: false, muted: false, loop: false });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create');
    }
  };

  const deleteEmbed = async (id) => {
    try {
      await api.delete(`/youtube/embeds/${id}`);
      toast.success('Embed removed');
      fetchData();
    } catch (err) { toast.error('Failed'); }
  };

  const copyEmbedCode = (embed) => {
    const code = `<iframe width="560" height="315" src="${embed.embed_url}" title="${embed.title}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>`;
    navigator.clipboard.writeText(code);
    toast.success('Embed code copied');
  };

  return (
    <div className="space-y-6" data-testid="youtube-embeds-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">YouTube Embeds</h1>
          <p className="text-sm text-zinc-500 mt-1">Embed YouTube videos with commerce tracking</p>
        </div>
        <Button onClick={() => setShowAdd(true)} data-testid="add-embed-btn" className="bg-red-600 hover:bg-red-700">
          <Plus size={16} className="mr-2" /> Add Embed
        </Button>
      </div>

      {/* Analytics */}
      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { label: 'Embeds', value: analytics.total_embeds },
            { label: 'Views', value: analytics.total_views },
            { label: 'Watch Starts', value: analytics.total_watch_starts },
            { label: 'Outbound Clicks', value: analytics.total_outbound_clicks },
            { label: 'Engagement', value: `${analytics.engagement_rate}%` },
          ].map((s, i) => (
            <Card key={i} className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="p-3">
                <p className="text-[10px] text-zinc-500 uppercase tracking-wider">{s.label}</p>
                <p className="text-lg font-bold mt-0.5">{s.value || 0}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Embed Form */}
      {showAdd && (
        <Card className="bg-zinc-900 border-zinc-700">
          <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><YoutubeLogo size={20} className="text-red-500" /> Add YouTube Embed</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input
                data-testid="youtube-url-input"
                value={urlInput}
                onChange={(e) => { setUrlInput(e.target.value); setValidated(null); }}
                placeholder="https://www.youtube.com/watch?v=..."
                className="flex-1 bg-zinc-800 border-zinc-700"
              />
              <Button onClick={validateUrl} data-testid="validate-url-btn" variant="outline" className="border-zinc-700 text-zinc-300">
                Validate
              </Button>
            </div>
            {validated && (
              <div className="flex items-center gap-2">
                {validated.valid ? (
                  <>
                    <Check size={16} className="text-emerald-400" />
                    <span className="text-sm text-emerald-400">Valid {validated.type}</span>
                    {validated.video_id && <Badge variant="outline" className="text-xs text-zinc-400 border-zinc-600 font-mono">{validated.video_id}</Badge>}
                  </>
                ) : (
                  <><XIcon size={16} className="text-red-400" /><span className="text-sm text-red-400">{validated.error}</span></>
                )}
              </div>
            )}
            {validated?.valid && (
              <>
                {validated.thumbnail && (
                  <div className="relative rounded-lg overflow-hidden bg-zinc-800 aspect-video max-w-sm">
                    <img src={validated.thumbnail} alt="Thumbnail" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                      <Play size={32} weight="fill" className="text-white/80" />
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <Input data-testid="embed-title" value={form.title} onChange={(e) => setForm(p => ({...p, title: e.target.value}))} placeholder="Title (optional)" className="bg-zinc-800 border-zinc-700" />
                  <Input value={form.description} onChange={(e) => setForm(p => ({...p, description: e.target.value}))} placeholder="Description" className="bg-zinc-800 border-zinc-700" />
                </div>
                <div className="flex flex-wrap gap-4 text-sm">
                  <label className="flex items-center gap-2 text-zinc-400 cursor-pointer">
                    <input type="number" value={form.start} onChange={(e) => setForm(p => ({...p, start: parseInt(e.target.value) || 0}))}
                      className="w-20 h-8 bg-zinc-800 border border-zinc-700 rounded px-2 text-sm text-zinc-300" min={0} />
                    Start (sec)
                  </label>
                  {['autoplay', 'muted', 'loop'].map(opt => (
                    <label key={opt} className="flex items-center gap-2 text-zinc-400 cursor-pointer">
                      <input type="checkbox" checked={form[opt]} onChange={(e) => setForm(p => ({...p, [opt]: e.target.checked}))}
                        className="rounded bg-zinc-800 border-zinc-700" />
                      {opt.charAt(0).toUpperCase() + opt.slice(1)}
                    </label>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Button onClick={createEmbed} data-testid="create-embed-btn" className="bg-red-600 hover:bg-red-700">Create Embed</Button>
                  <Button variant="ghost" onClick={() => { setShowAdd(false); setValidated(null); }} className="text-zinc-400">Cancel</Button>
                </div>
              </>
            )}
            {!validated && !urlInput && (
              <Button variant="ghost" onClick={() => setShowAdd(false)} className="text-zinc-400">Cancel</Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Embeds List */}
      {loading ? (
        <div className="text-center text-zinc-500 py-8">Loading embeds...</div>
      ) : embeds.length === 0 ? (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-8 text-center">
            <YoutubeLogo size={40} className="mx-auto text-zinc-600 mb-2" />
            <p className="text-zinc-500">No YouTube embeds yet. Add your first embed above.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {embeds.map(emb => (
            <Card key={emb.embed_id} className="bg-zinc-900/50 border-zinc-800 hover:border-zinc-700 transition-colors overflow-hidden" data-testid={`embed-${emb.embed_id}`}>
              {/* Thumbnail / Preview */}
              <div className="relative bg-zinc-800 aspect-video cursor-pointer" onClick={() => setPreviewEmbed(previewEmbed === emb.embed_id ? null : emb.embed_id)}>
                {previewEmbed === emb.embed_id ? (
                  <iframe src={emb.embed_url} title={emb.title} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                ) : (
                  <>
                    {emb.thumbnail ? (
                      <img src={emb.thumbnail} alt={emb.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"><YoutubeLogo size={40} className="text-zinc-600" /></div>
                    )}
                    <div className="absolute inset-0 flex items-center justify-center bg-black/20 hover:bg-black/10 transition-colors">
                      <div className="w-12 h-8 bg-red-600 rounded-lg flex items-center justify-center">
                        <Play size={18} weight="fill" className="text-white" />
                      </div>
                    </div>
                  </>
                )}
                <Badge className="absolute top-2 left-2 text-[10px] bg-red-900/60 text-red-300">{emb.embed_type}</Badge>
              </div>
              <CardContent className="p-3">
                <h3 className="text-sm font-medium text-zinc-200 truncate">{emb.title || emb.youtube_video_id || 'Untitled'}</h3>
                {emb.channel_name && <p className="text-[10px] text-zinc-500">{emb.channel_name}</p>}
                <div className="flex items-center gap-2 mt-1 text-[10px] text-zinc-500">
                  <span><Eye size={10} className="inline mr-0.5" />{emb.views || 0}</span>
                  <span><Play size={10} className="inline mr-0.5" />{emb.watch_starts || 0} starts</span>
                  <span><LinkSimple size={10} className="inline mr-0.5" />{emb.outbound_clicks || 0} clicks</span>
                </div>
                <div className="flex gap-1 mt-2">
                  <Button size="sm" variant="ghost" onClick={() => copyEmbedCode(emb)} className="h-7 px-2 text-xs text-blue-400 hover:text-blue-300">
                    <Copy size={12} className="mr-1" /> Code
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => deleteEmbed(emb.embed_id)} className="h-7 px-2 text-xs text-red-400 hover:text-red-300 hover:bg-red-900/20 ml-auto">
                    <Trash size={12} />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
